import Image from 'next/future/image';
import { Dispatch, SetStateAction } from 'react';
import { RC_DURATION } from '@lambda/reebelo';
import Loading from '@/components/Loading/Loading';
import {
  multipleWarrantyUpsellTitles,
  warrantyBenefitsList,
} from '@/lib/reebelocare/constants';
import { generateWarrantyUpsellCta } from '@/lib/reebelocare/helpers';
import checkIcon from '@/public/icons/checkout/check.svg';
import { ProductWarrantyT } from '@/lib/reebelocare/types';
import USLineItem from './USLineItem';
import reebeloCareIcon from '@/public/icons/reebelocare/reebelocare.svg';
import WarrantyDisclaimer from '@/components/collections/[handle]/WarrantyDisclaimer';
import {
  trackReebeloCareDeselected,
  trackReebeloCareSelected,
} from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';
import settings from '@/settings';

const AdditionalSingleDevice = ({
  url,
  title,
  productWarranty,
  isCheckingOut,
  onContinue,
  setIsCheckingOut,
  onRcContinue,
  checked,
  setChecked,
  trackingProps,
}: {
  url: string;
  title: string;
  productWarranty: ProductWarrantyT;
  isCheckingOut: boolean;
  onContinue: () => void;
  setIsCheckingOut: Dispatch<SetStateAction<boolean>>;
  onRcContinue: () => Promise<void>;
  checked: RC_DURATION;
  setChecked: Dispatch<SetStateAction<RC_DURATION>>;
  trackingProps: (_duration: RC_DURATION) => ReebeloCareTrackRequest;
}) => {
  const warrantyTitle =
    multipleWarrantyUpsellTitles?.[settings.store] ??
    multipleWarrantyUpsellTitles.default;

  const warrantyBenefits =
    warrantyBenefitsList?.[settings.store] ?? warrantyBenefitsList.default;

  return (
    <section className="text-sm">
      <div className="flex flex-col items-center gap-5 bg-[#ECFCF5] p-4 sm:flex-row md:p-8">
        <div className="relative mx-auto h-[110px] w-[110px] rounded-xl sm:h-[125px] sm:w-[125px]">
          <div className="h-full w-full">
            <Image
              src={url}
              alt={title}
              height={300}
              width={300}
              priority
              loading="eager"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="absolute right-0 bottom-[-10px] h-[50px] w-[50px] sm:right-[-10px] sm:h-[60px] sm:w-[60px]">
            <Image
              src={reebeloCareIcon}
              alt="ReebeloCare"
              height={300}
              width={300}
              className="h-full w-full object-contain"
            />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="font-bold">{warrantyTitle}</h3>
          <ul className="mt-3 space-y-1">
            {warrantyBenefits.map((benefit) => (
              <li key={benefit.label} className="flex items-center gap-2">
                <div className="h-4 w-4">
                  <Image
                    src={checkIcon}
                    height={50}
                    width={50}
                    alt="Check"
                    className="h-full w-full object-cover"
                  />
                </div>
                {benefit.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="p-4 md:p-8">
        <div className="space-y-2">
          {Object.keys(productWarranty).map((d) => {
            const duration = d as RC_DURATION;

            if (!productWarranty[duration]) return null;

            return (
              <USLineItem
                key={duration}
                productWarranty={productWarranty}
                checked={checked}
                duration={duration}
                isCheckingOut={isCheckingOut}
                handleToggle={() =>
                  setChecked((prevDuration) => {
                    trackReebeloCareDeselected(trackingProps(prevDuration));
                    trackReebeloCareSelected(trackingProps(duration));

                    return duration;
                  })
                }
              />
            );
          })}
        </div>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row">
          <button
            onClick={() => {
              setIsCheckingOut(true);
              onContinue();
            }}
            type="button"
            className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-white text-xs font-bold text-gray-700 underline-offset-2 outline-none hover:underline"
          >
            {generateWarrantyUpsellCta().reject}
          </button>
          <button
            disabled={isCheckingOut}
            type="button"
            className="flex h-10 w-full items-center justify-center rounded-lg border border-gray-700 bg-gray-700 text-xs font-bold text-white underline-offset-2 outline-none hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onRcContinue()}
          >
            {isCheckingOut ? (
              <Loading size="xs" />
            ) : (
              generateWarrantyUpsellCta().accept
            )}
          </button>
        </div>
        <WarrantyDisclaimer className="mt-2.5" />
      </div>
    </section>
  );
};

export default AdditionalSingleDevice;
